export const REDACTED_VALUE = '[REDACTED]';

// Public routes whose next path segment is an access secret (share token, webhook secret, one-time code).
// No leading /api: the same prefix has to match /api/v1/… and the absolute URLs of outgoing client spans.
const SECRET_PATH_PREFIXES: readonly string[] = [
  '/partner-shop/webhook/',
  '/projects/token-links/',
  '/client-books/',
  '/prints/',
  '/prints/access/',
  '/prints/client/',
  '/print-services/access/',
  '/gallery/entry/',
  '/gallery/view/',
  '/gallery/persons/',
  '/gallery/slideshow/',
  '/gallery-folder-public/',
  '/gallery-social/archive/',
  '/studio/public/sign/',
  '/studio/public/messaging/telegram/webhook/',
  '/auth/impersonate/session/',
];
// A secret segment also ends at quotes, commas and closing brackets, so the tail of a JSON or HTML string survives.
const SEGMENT_END = '?#\\s"\'`,)}\\]<>';
const SECRET_SEGMENT = `[^/${SEGMENT_END}]+`;
// nginx decodes %2F before it matches a location, so an encoded slash still separates the words of a prefix.
const SLASH = '(?:/|%2f)';
// Matrix parameters (;k=v) Angular drops; no «;» or %2f inside, else a long «;;;…» run backtracks exponentially.
const MATRIX = `(?:;(?:[^;/%${SEGMENT_END}]|%(?!2f))*)*`;
// An optional API version word, v<n>/, the digits percent-encoded or not.
const API_VERSION = `(?:${tolerantPath('v')}(?:\\d|%3\\d)+${MATRIX}${SLASH})?`;
// /api/(v<n>/)?projects/links/ — a class link; the personal code pattern checks it again behind /code/.
const CLASS_LINK_PREFIX = `${tolerantPath('/api/')}${API_VERSION}${tolerantPath('projects/links/')}`;
// Prefixes too generic as literals (bare /projects/links/ also hits the non-secret /api/studio/projects/links/<id>),
// so they are anchored patterns; only (?:…) groups inside, or $1 stops being the whole prefix.
const SECRET_PATH_PREFIX_PATTERNS: readonly string[] = [
  String.raw`${CLASS_LINK_PREFIX}`,
  // Lookbehind reads the original input, so the code after an already redacted class link still matches.
  String.raw`\/${tolerantPath('code/')}(?<=${CLASS_LINK_PREFIX}${SECRET_SEGMENT}\/${tolerantPath('code/')})`,
  // A raw «/» first: the id segment runs over %2f, so an encoded start would rescan it from every %2fprojects%2f.
  String.raw`\/${tolerantPath('projects/')}${SECRET_SEGMENT}\/${tolerantPath('coordinator/')}`,
];

// extraPrefixes: routes only the caller sees, e.g. frontend page paths.
export function createSecretPathRedactor(extraPrefixes: readonly string[] = []): (url: string) => string {
  // Longest first: the first fitting alternative wins, so /client/ ahead of /client/t/ would redact the «t».
  const literalPrefixes: string[] = [...extraPrefixes, ...SECRET_PATH_PREFIXES].sort((a, b) => b.length - a.length);
  // A segment starting with «:» is an Express placeholder (http.route, logged templates), never a secret;
  // operator-set webhook secrets must not start with «:». «[» opens an already redacted one ([REDACTED], [id]).
  const secretPathSegment = new RegExp(
    `(${[...literalPrefixes.map(tolerantPath), ...SECRET_PATH_PREFIX_PATTERNS].join('|')})(?![:[])${SECRET_SEGMENT}`,
    'gi',
  );

  return (url: string): string => {
    // Error fields reach here untyped: a subclass may carry a non-string message or stack.
    if (typeof url !== 'string') {
      return url;
    }

    return url.replace(secretPathSegment, `$1${REDACTED_VALUE}`);
  };
}

// A path literal as nginx and Angular still route it; the input is not decoded, so $1 keeps its spelling.
function tolerantPath(literal: string): string {
  const words: string[] = literal.split('/');

  return words
    .map((word, index) => {
      const pattern: string = [...word].map(tolerantCharacter).join('');
      const isLast: boolean = index === words.length - 1;

      return isLast ? pattern : `${pattern}${word ? MATRIX : ''}${SLASH}`;
    })
    .join('');
}

// Hex of both cases: %63 and %43 both decode to a letter the «i» flag matches; %2570 is not decoded twice.
function tolerantCharacter(character: string): string {
  const hexes: string[] = [...new Set([character.toLowerCase(), character.toUpperCase()].map(hexOf))];

  return `(?:${escapeRegExp(character)}|%(?:${hexes.join('|')}))`;
}

function hexOf(character: string): string {
  return character.charCodeAt(0).toString(16).padStart(2, '0');
}

function escapeRegExp(literal: string): string {
  return literal.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
